import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  ArrowLeft,
  Printer,
  Trash2,
  ReceiptText,
  CalendarRange,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { normalizeListResponse } from "@/lib/listResponse";
import { useAuth } from "@/context/AuthContext";

const input =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";
const button =
  "inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2 text-sm hover:bg-secondary disabled:opacity-50";
const today = () => new Date().toISOString().slice(0, 10);
const currencies = ["INR", "USD", "EUR", "GBP", "AED", "CAD", "AUD"];
const label = (s) => s.replaceAll("_", " ");
const money = (v, c) =>
  new Intl.NumberFormat("en", {
    style: "currency",
    currency: c || "INR",
  }).format(v || 0);
const errorMessage = (e) => {
  const detail = e.response?.data?.detail;
  return Array.isArray(detail)
    ? detail.map((d) => `${d.loc.slice(1).join(" / ")}: ${d.msg}`).join("; ")
    : detail || e.message;
};
const phase = () => ({
  title: "",
  deliverables: "",
  start_date: today(),
  end_date: today(),
  quantity: 1,
  unit: "fixed",
  rate: 0,
  payment_trigger: "On milestone acceptance",
  acceptance_criteria: "",
  dependencies: "",
});
const fresh = (expense) =>
  expense
    ? {
        title: "",
        vendor: "",
        expense_date: today(),
        due_date: "",
        category: "other",
        currency: "INR",
        amount: "",
        tax: 0,
        tax_id: "",
        reference: "",
        department: "",
        project_id: "",
        paid_by: "",
        reimbursable: false,
        billable: false,
        recurrence: "none",
        status: "draft",
        payment_date: "",
        payment_method: "bank_transfer",
        transaction_id: "",
        receipt_url: "",
        notes: "",
      }
    : {
        title: "",
        project_id: "",
        client_id: "",
        currency: "INR",
        valid_until: today(),
        status: "draft",
        scope: "",
        assumptions: "",
        exclusions: "",
        change_policy:
          "Changes to approved scope require a written estimate and approval.",
        payment_terms: "Payment due within 15 days of each milestone invoice.",
        discount: 0,
        tax_rate: 0,
        phases: [phase()],
      };

export function Field({
  title,
  value,
  onChange,
  type = "text",
  options,
  required = false,
  min,
  max,
  children,
}) {
  return (
    <label className="block space-y-1.5 text-sm">
      <span className="font-medium">
        {title}
        {required && <span className="text-primary"> *</span>}
      </span>
      {options ? (
        <select
          className={input}
          aria-label={title}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        >
          {options.map((o) => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? label(o)}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          className={input}
          aria-label={title}
          rows={3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        />
      ) : (
        <input
          className={input}
          aria-label={title}
          type={type}
          step={type === "number" ? "0.01" : undefined}
          min={min}
          max={max}
          required={required}
          value={value ?? ""}
          onChange={(e) =>
            onChange(
              type === "number" && e.target.value !== ""
                ? Number(e.target.value)
                : e.target.value,
            )
          }
        />
      )}
      {children}
    </label>
  );
}
function Section({ title, description, children }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

export default function Finance({ mode = "expenses" }) {
  const expense = mode === "expenses";
  const endpoint = expense ? "/expenses" : "/project-estimates";
  const { user } = useAuth();
  const [items, setItems] = useState([]),
    [clients, setClients] = useState([]),
    [projects, setProjects] = useState([]);
  const [form, setForm] = useState(null),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [q, setQ] = useState(""),
    [status, setStatus] = useState(""),
    [page, setPage] = useState(1),
    [total, setTotal] = useState(0);
  const statuses = expense
    ? ["draft", "submitted", "approved", "rejected", "paid"]
    : ["draft", "shared", "accepted", "rejected"];
  const module = expense ? "expenses" : "project_pricing";
  const can = (action) =>
    ["admin", "super_admin"].includes(user?.role) ||
    (user?.permissions?.[module]?.[action] ??
      (expense ? ["accounts"] : ["accounts", "project_manager"]).includes(
        user?.role,
      ));
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoint, {
        params: { q, status, page, limit: 25 },
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, q, status, page]);
  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    setForm(null);
    setPreview(null);
    setPage(1);
    setStatus("");
    setQ("");
    Promise.all([api.get("/clients"), api.get("/projects")])
      .then(([c, p]) => {
        setClients(normalizeListResponse(c.data).items);
        setProjects(normalizeListResponse(p.data).items);
      })
      .catch((e) => toast.error(errorMessage(e)));
  }, [mode]);
  const set = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: value,
      ...(key === "client_id" ? { project_id: "" } : {}),
    }));
  const field = (key, title, props = {}) => (
    <Field
      key={key}
      title={title}
      value={form[key]}
      onChange={(v) => set(key, v)}
      {...props}
    />
  );
  const subtotal = expense
    ? Number(form?.amount || 0)
    : (form?.phases || []).reduce(
        (sum, p) => sum + Math.round(p.quantity * p.rate * 100) / 100,
        0,
      );
  const tax = expense
    ? Number(form?.tax || 0)
    : Math.round(
        (subtotal - Number(form?.discount || 0)) * Number(form?.tax_rate || 0),
      ) / 100;
  const grandTotal =
    Math.round((subtotal - Number(form?.discount || 0) + tax) * 100) / 100;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (expense) {
        payload.due_date = payload.due_date || null;
        payload.payment_date = payload.payment_date || null;
      }
      if (form.id) await api.put(`${endpoint}/${form.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(expense ? "Expense saved" : "Project estimate saved");
      setForm(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (preview)
    return (
      <EstimatePreview
        estimate={preview}
        clients={clients}
        projects={projects}
        onClose={() => setPreview(null)}
      />
    );
  if (form)
    return (
      <form onSubmit={save} className="max-w-6xl mx-auto space-y-5 pb-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <button
              type="button"
              className="text-sm text-muted-foreground inline-flex items-center gap-2 mb-3"
              onClick={() => setForm(null)}
            >
              <ArrowLeft size={16} /> Back to{" "}
              {expense ? "expenses" : "project pricing"}
            </button>
            <h1 className="text-2xl font-semibold">
              {form.id ? "Edit" : "New"}{" "}
              {expense ? "office expense" : "project estimate"}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {expense
                ? "Capture the cost, supporting receipt, and payment details."
                : "Define the scope, price each phase, and agree a delivery schedule."}
            </p>
          </div>
          <button
            className={`${button} bg-primary text-primary-foreground`}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save " + (expense ? "expense" : "estimate")}
          </button>
        </div>
        <div className="grid lg:grid-cols-[1fr_280px] gap-5 items-start">
          <div className="space-y-5">
            <Section
              title={expense ? "1. Expense details" : "1. Client & project"}
            >
              {field("title", expense ? "Expense title" : "Estimate title", {
                required: true,
              })}
              {expense
                ? field("vendor", "Vendor / payee", { required: true })
                : field("client_id", "Client", {
                    required: true,
                    options: [
                      { value: "", label: "Select client" },
                      ...clients.map((c) => ({
                        value: c.id,
                        label: c.company || c.client_name,
                      })),
                    ],
                  })}
              {expense
                ? field("category", "Category", {
                    options: [
                      "rent",
                      "utilities",
                      "software",
                      "hardware",
                      "payroll",
                      "travel",
                      "meals",
                      "marketing",
                      "professional_services",
                      "office_supplies",
                      "other",
                    ],
                  })
                : field("valid_until", "Quote valid until", {
                    type: "date",
                    required: true,
                  })}
              {field("project_id", expense ? "Project (optional)" : "Project", {
                required: !expense,
                options: [
                  { value: "", label: "Select project" },
                  ...projects
                    .filter((p) => expense || p.client_id === form.client_id)
                    .map((p) => ({ value: p.id, label: p.name })),
                ],
              })}
              {field("currency", "Currency", { options: currencies })}
              {field("status", "Status", {
                options: statuses.filter(
                  (s) =>
                    !expense ||
                    ["admin", "super_admin", "accounts"].includes(user?.role) ||
                    ["draft", "submitted"].includes(s),
                ),
              })}
              {expense &&
                field("expense_date", "Expense date", {
                  type: "date",
                  required: true,
                })}
              {expense && field("due_date", "Due date", { type: "date" })}
            </Section>
            {expense ? (
              <>
                <Section
                  title="2. Amount & allocation"
                  description="Enter the net amount and tax separately. Totals stay in the expense currency."
                >
                  {field("amount", "Net amount", {
                    type: "number",
                    min: 0.01,
                    required: true,
                  })}
                  {field("tax", "Tax amount", {
                    type: "number",
                    min: 0,
                    required: true,
                  })}
                  {field("reference", "Vendor invoice / receipt number")}
                  {field("tax_id", "Vendor tax ID / GSTIN")}
                  {field("department", "Department / cost center")}
                  {field("paid_by", "Employee / paid by")}
                  {["reimbursable", "billable"].map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form[k]}
                        onChange={(e) => set(k, e.target.checked)}
                      />
                      {k === "reimbursable"
                        ? "Employee reimbursement required"
                        : "Billable to client"}
                    </label>
                  ))}
                </Section>
                <Section title="3. Payment & supporting documents">
                  {field("payment_method", "Payment method", {
                    options: [
                      "bank_transfer",
                      "cash",
                      "card",
                      "upi",
                      "cheque",
                      "other",
                    ],
                  })}
                  {field("payment_date", "Payment date", {
                    type: "date",
                    min: form.expense_date,
                    required: form.status === "paid",
                  })}
                  {field("transaction_id", "Transaction reference")}
                  {field("receipt_url", "Receipt link (HTTPS)", {
                    type: "url",
                  })}
                  {field("recurrence", "Recurring cost", {
                    options: ["none", "monthly", "quarterly", "yearly"],
                  })}
                  <p className="text-sm text-muted-foreground self-center">
                    Recurrence is a planning label; record each actual payment
                    as a separate expense.
                  </p>
                  <div className="md:col-span-2">
                    {field("notes", "Notes / business purpose", {
                      type: "textarea",
                    })}
                  </div>
                </Section>
              </>
            ) : (
              <>
                <Section title="2. Scope & boundaries">
                  <div className="md:col-span-2">
                    {field("scope", "Project scope", {
                      type: "textarea",
                      required: true,
                    })}
                  </div>
                  {field(
                    "assumptions",
                    "Assumptions & client responsibilities",
                    { type: "textarea" },
                  )}
                  {field("exclusions", "Out of scope", { type: "textarea" })}
                </Section>
                <section className="rounded-xl border border-border bg-card p-5 space-y-4">
                  <h2 className="font-semibold">
                    3. Price breakdown & delivery timeline
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Each phase defines its deliverables, scheduled dates, and
                    billing trigger. Tax and discount apply to the whole
                    estimate.
                  </p>
                  {form.phases.map((p, i) => {
                    const update = (key, value) =>
                      set(
                        "phases",
                        form.phases.map((item, j) =>
                          j === i ? { ...item, [key]: value } : item,
                        ),
                      );
                    const pf = (key, title, props = {}) => (
                      <Field
                        title={title}
                        value={p[key]}
                        onChange={(v) => update(key, v)}
                        {...props}
                      />
                    );
                    return (
                      <div
                        key={i}
                        className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20"
                      >
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-sm">
                            Phase {i + 1}
                          </h3>
                          <button
                            type="button"
                            aria-label={`Remove phase ${i + 1}`}
                            disabled={form.phases.length === 1}
                            className={button}
                            onClick={() =>
                              set(
                                "phases",
                                form.phases.filter((_, j) => j !== i),
                              )
                            }
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {pf("title", "Phase name", { required: true })}
                          {pf("deliverables", "Deliverables", {
                            type: "textarea",
                            required: true,
                          })}
                          {pf("start_date", "Start date", {
                            type: "date",
                            required: true,
                          })}
                          {pf("end_date", "End date", {
                            type: "date",
                            min: p.start_date,
                            required: true,
                          })}
                          {pf("unit", "Pricing unit", {
                            options: ["fixed", "hours", "days"],
                          })}
                          {pf("quantity", "Quantity / effort", {
                            type: "number",
                            min: 0.01,
                            required: true,
                          })}
                          {pf("rate", "Unit rate", {
                            type: "number",
                            min: 0,
                            required: true,
                          })}
                          <div className="self-center text-lg font-semibold">
                            {money(p.quantity * p.rate, form.currency)}
                          </div>
                          {pf("payment_trigger", "Billing trigger", {
                            required: true,
                          })}
                          {pf("dependencies", "Dependencies")}
                          {pf("acceptance_criteria", "Acceptance criteria", {
                            type: "textarea",
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    className={button}
                    onClick={() => set("phases", [...form.phases, phase()])}
                  >
                    <Plus size={16} /> Add phase
                  </button>
                </section>
                <Section title="4. Commercial terms">
                  {field("discount", "Discount amount", {
                    type: "number",
                    min: 0,
                    max: subtotal,
                    required: true,
                  })}
                  {field("tax_rate", "Tax rate (%)", {
                    type: "number",
                    min: 0,
                    max: 100,
                    required: true,
                  })}
                  {field("payment_terms", "Payment terms", {
                    type: "textarea",
                  })}
                  {field("change_policy", "Scope change policy", {
                    type: "textarea",
                  })}
                </Section>
              </>
            )}
          </div>
          <aside className="lg:sticky lg:top-5 rounded-xl border border-border bg-card p-5 space-y-4">
            <h2 className="font-semibold">
              {expense ? "Expense summary" : "Estimate summary"}
            </h2>
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{money(subtotal, form.currency)}</span>
            </div>
            {!expense && (
              <div className="flex justify-between text-sm">
                <span>Discount</span>
                <span>−{money(form.discount, form.currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span>Tax</span>
              <span>{money(tax, form.currency)}</span>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground">
                Total · {form.currency}
              </p>
              <p className="text-2xl font-semibold mt-1">
                {money(grandTotal, form.currency)}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {expense
                ? "Paid expenses are locked to preserve the audit trail."
                : "Accepted estimates are locked. Create a new estimate when the scope changes. Shared and accepted statuses record offline client communication."}
            </p>
            <button
              className={`${button} w-full bg-primary text-primary-foreground`}
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </aside>
        </div>
      </form>
    );
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary mb-2">
            Finance & delivery
          </div>
          <h1 className="text-3xl font-semibold flex items-center gap-3">
            {expense ? <ReceiptText /> : <CalendarRange />}
            {expense ? "Office expenses" : "Project pricing"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {expense
              ? "Track operating costs, approvals, and reimbursements in one place."
              : "Turn project scope into a clear price breakdown and delivery plan."}
          </p>
        </div>
        {can("create") && (
          <button
            className={`${button} bg-primary text-primary-foreground`}
            onClick={() => setForm(fresh(expense))}
          >
            <Plus size={16} />
            {expense ? "New expense" : "New estimate"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <input
          aria-label="Search by title"
          className={`${input} sm:max-w-sm`}
          placeholder="Search by title…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          aria-label="Filter status"
          className={`${input} sm:max-w-48`}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>
      {error ? (
        <div role="alert" className="p-5 border border-destructive rounded-lg">
          {error}
          <button onClick={load} className={`${button} ml-4`}>
            Retry
          </button>
        </div>
      ) : loading ? (
        <p className="p-8 text-muted-foreground">Loading…</p>
      ) : !items.length ? (
        <div className="text-center border border-dashed rounded-xl p-16">
          <h2 className="font-semibold">
            {q || status
              ? "No matching records"
              : expense
                ? "Your expense register starts here"
                : "Build your first project estimate"}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {q || status
              ? "Try another search or status."
              : expense
                ? "Add office costs with a category, receipt, and payment status."
                : "Select a client project, define phases, and prepare a printable plan."}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-muted-foreground">
              <tr>
                {[
                  expense ? "Expense / vendor" : "Estimate / project",
                  expense ? "Date / category" : "Valid until",
                  "Status",
                  "Total",
                  "Actions",
                ].map((h) => (
                  <th key={h} className="text-left px-5 py-4 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-border">
                  <td className="px-5 py-4">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {expense
                        ? item.vendor
                        : projects.find((p) => p.id === item.project_id)?.name}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    {expense ? item.expense_date : item.valid_until}
                    {expense && (
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        {label(item.category)}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="rounded-full bg-secondary px-3 py-1 text-xs capitalize">
                      {item.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 font-mono whitespace-nowrap">
                    {money(item.total, item.currency)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2">
                      {can("edit") &&
                        !(expense
                          ? item.status === "paid"
                          : item.status === "accepted") && (
                          <button
                            className={button}
                            onClick={() => setForm(item)}
                          >
                            Edit
                          </button>
                        )}
                      {expense ? (
                        <button
                          className={button}
                          onClick={() => setPreview(item)}
                        >
                          Details
                        </button>
                      ) : (
                        <button
                          className={button}
                          onClick={() => setPreview(item)}
                        >
                          Client preview
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between text-sm">
        <span>
          {total} records · Page {page} of {Math.max(1, Math.ceil(total / 25))}
        </span>
        <div className="flex gap-2">
          <button
            className={button}
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <button
            className={button}
            disabled={page * 25 >= total}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function EstimatePreview({ estimate: e, clients, projects, onClose }) {
  const expense = !e.phases;
  const start =
    !expense && Math.min(...e.phases.map((p) => Date.parse(p.start_date)));
  const end =
    !expense && Math.max(...e.phases.map((p) => Date.parse(p.end_date)));
  const span = Math.max(0, end - start);
  return (
    <div className="finance-preview max-w-5xl mx-auto space-y-6">
      <div className="flex justify-between print:hidden">
        <button className={button} onClick={onClose}>
          <ArrowLeft size={16} /> Back
        </button>
        <button className={button} onClick={() => window.print()}>
          <Printer size={16} /> Print / Save PDF
        </button>
      </div>
      <article className="finance-document bg-card border rounded-xl p-6 md:p-10 space-y-8">
        <header className="border-b pb-6">
          <p className="text-primary text-xs uppercase tracking-widest">
            {expense ? "Expense record" : "Project estimate & delivery plan"}
          </p>
          <h1 className="text-3xl font-semibold mt-3">{e.title}</h1>
          <p className="text-muted-foreground mt-2">
            {expense
              ? e.vendor
              : clients.find((c) => c.id === e.client_id)?.company}{" "}
            · {e.currency} · {e.status}
          </p>
          <p className="text-sm mt-2">
            {expense
              ? `Expense date: ${e.expense_date}`
              : `Project: ${projects.find((p) => p.id === e.project_id)?.name || ""} · Valid until: ${e.valid_until}`}
          </p>
        </header>
        {expense ? (
          <dl className="grid md:grid-cols-2 gap-4">
            {[
              "category",
              "reference",
              "department",
              "paid_by",
              "tax_id",
              "payment_date",
              "payment_method",
              "transaction_id",
              "recurrence",
              "notes",
              "reimbursable",
              "billable",
            ].map((k) => (
              <div key={k}>
                <dt className="capitalize text-xs text-muted-foreground">
                  {label(k)}
                </dt>
                <dd className="mt-1 whitespace-pre-wrap">
                  {typeof e[k] === "boolean"
                    ? e[k]
                      ? "Yes"
                      : "No"
                    : e[k] || "—"}
                </dd>
              </div>
            ))}
            {e.receipt_url && (
              <a
                className="text-primary underline"
                href={e.receipt_url}
                target="_blank"
                rel="noreferrer"
              >
                Open receipt
              </a>
            )}
          </dl>
        ) : (
          <>
            <section>
              <h2 className="font-semibold mb-2">Scope</h2>
              <p className="whitespace-pre-wrap text-sm">{e.scope}</p>
            </section>
            <section className="space-y-4">
              <h2 className="font-semibold">
                Delivery timeline & price breakdown
              </h2>
              {e.phases.map((p, i) => (
                <div
                  key={i}
                  className="border rounded-lg p-4 space-y-2 break-inside-avoid"
                >
                  <div className="flex justify-between gap-4">
                    <h3 className="font-semibold">
                      {i + 1}. {p.title}
                    </h3>
                    <span className="font-mono whitespace-nowrap">
                      {money(p.quantity * p.rate, e.currency)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {p.start_date} → {p.end_date} · {p.quantity} {p.unit} ×{" "}
                    {money(p.rate, e.currency)}
                  </p>
                  <div className="h-2 rounded bg-secondary overflow-hidden">
                    <div
                      className="h-full bg-primary rounded"
                      style={{
                        marginLeft: `${((Date.parse(p.start_date) - start) / (span + 86400000)) * 100}%`,
                        width: `${((Date.parse(p.end_date) - Date.parse(p.start_date) + 86400000) / (span + 86400000)) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-sm whitespace-pre-wrap">
                    {p.deliverables}
                  </p>
                  <p className="text-xs">Billing: {p.payment_trigger}</p>
                  {p.dependencies && (
                    <p className="text-xs">Dependencies: {p.dependencies}</p>
                  )}
                  {p.acceptance_criteria && (
                    <p className="text-xs whitespace-pre-wrap">
                      Acceptance: {p.acceptance_criteria}
                    </p>
                  )}
                </div>
              ))}
            </section>
            {["assumptions", "exclusions", "payment_terms", "change_policy"]
              .filter((k) => e[k])
              .map((k) => (
                <section key={k}>
                  <h2 className="font-semibold capitalize mb-2">{label(k)}</h2>
                  <p className="text-sm whitespace-pre-wrap">{e[k]}</p>
                </section>
              ))}
          </>
        )}
        <div className="border-t pt-5 space-y-2 text-right">
          <p>Subtotal: {money(expense ? e.amount : e.subtotal, e.currency)}</p>
          {!expense && <p>Discount: −{money(e.discount, e.currency)}</p>}
          <p>Tax: {money(e.tax, e.currency)}</p>
          <p className="text-2xl font-semibold">
            Total: {money(e.total, e.currency)}
          </p>
        </div>
        {expense && (
          <section>
            <h2 className="font-semibold">Audit history</h2>
            {e.history?.map((h, i) => (
              <p key={i} className="text-xs text-muted-foreground mt-2">
                {new Date(h.at).toLocaleString()} · {h.status} · User {h.by}
              </p>
            ))}
          </section>
        )}
        <footer className="text-xs text-muted-foreground border-t pt-4">
          Reference: {e.id}
        </footer>
      </article>
    </div>
  );
}
